import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Lock, AlertTriangle, User, Home } from "lucide-react";

import { fetchTemplateAudiences } from "../api/communicationsApi";

/**
 * "Choose template" - the categorised, searchable template picker.
 *
 * Christian Gravias (Monday 12918907065): "Instead of the pop-up in your
 * screenshot simply being 'Start from an SMS template' with a long list
 * underneath, I'd make it: Choose template / Buyers | Sellers | Vendors |
 * Landlords | Advocates | Portal | Past Clients | General ... And put a search
 * bar at the top. Agents will end up with 50-100 templates, so a single
 * scrolling list will eventually become annoying."
 *
 * ONE component for all four composers (Campaign email, Campaign SMS, Find
 * Buyers email, Find Buyers SMS). Those were three separate copies of the same
 * flat list, which is why the library could not simply be "added": each copy
 * would have needed its own tabs, its own search and its own required-tag hint.
 *
 * The rows also carry a `required_tags` warning. Picking "Auction Reminder" for
 * an audience with no property attached is the mistake that sends "goes to
 * auction  at ." to a few thousand people, so the picker says so on the row
 * BEFORE it is applied, and the composer's pre-send guard says so again after.
 */
export default function TemplatePickerModal({
  open,
  onClose,
  templates,
  onPick,
  channel = "sms",
  /** Compose-scope tags the composer CAN currently fill (property + agent). */
  resolvedTags = {},
  /** Extra z-index for the Find Buyers modal, which opens inside another modal. */
  zIndex = 50,
}) {
  const [audience, setAudience] = useState("all");
  const [search, setSearch] = useState("");
  const [audiences, setAudiences] = useState([]);
  const searchRef = useRef(null);

  const isEmail = channel === "email";

  // Tabs come from the backend's AUDIENCE_CHOICES, not a list retyped here, so a
  // new audience never needs a matching FE edit to become visible.
  useEffect(() => {
    if (!open) return;
    fetchTemplateAudiences(channel)
      .then((d) => setAudiences(d?.audiences || []))
      .catch(() => setAudiences([]));
  }, [open, channel]);

  // Reset per opening, and focus the search box - with 50-100 templates, typing
  // is faster than hunting, which is the whole point of the redesign.
  useEffect(() => {
    if (!open) return;
    setAudience("all");
    setSearch("");
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  const body = (t) => (isEmail ? t.subject || t.preview_text : t.sms_body) || "";

  // Filtered client-side: the list is already loaded for the channel, so tabbing
  // and typing stay instant instead of round-tripping per keystroke. The server
  // supports the same filters for when the library outgrows one fetch.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (templates || []).filter((t) => {
      if (audience !== "all" && (t.audience || "general") !== audience) return false;
      if (!q) return true;
      return (
        (t.name || "").toLowerCase().includes(q) ||
        body(t).toLowerCase().includes(q) ||
        (t.audience_display || "").toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, audience, search, isEmail]);

  // Two sections: the shared Collings library, then what this agent saved.
  const library = filtered.filter((t) => t.is_library);
  const mine = filtered.filter((t) => !t.is_library);

  // Only offer a tab that has something in it, so an agent never clicks
  // "Current Landlords" and finds an empty pane.
  const visibleTabs = useMemo(() => {
    const counts = {};
    (templates || []).forEach((t) => {
      const key = t.audience || "general";
      counts[key] = (counts[key] || 0) + 1;
    });
    const known = audiences.filter((a) => counts[a.value]);
    return [{ value: "all", label: "All", count: (templates || []).length }, ...known.map((a) => ({ ...a, count: counts[a.value] }))];
  }, [templates, audiences]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4"
      style={{ zIndex }}
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div>
            <h3 className="font-bold text-gray-900">Choose template</h3>
            <p className="text-xs text-gray-400">
              {isEmail ? "Email" : "SMS"} templates - the shared Collings library plus your own
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar (Christian: "put a search bar at the top") */}
        <div className="px-4 pt-3">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates"
              aria-label="Search templates"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2DC8DB]/40"
            />
          </div>
        </div>

        {/* Audience tabs */}
        <div className="px-4 pt-3 pb-2 flex flex-wrap gap-1.5 border-b border-gray-100">
          {visibleTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setAudience(tab.value)}
              // Explicit label: the tab text also appears as the audience badge
              // on every matching row, so without this the tab and the rows are
              // indistinguishable to assistive tech (and to the tests).
              aria-label={`Show ${tab.label} templates`}
              aria-pressed={audience === tab.value}
              className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${
                audience === tab.value
                  ? "bg-[#2DC8DB] text-white border-[#2DC8DB]"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {tab.label}
              <span className={audience === tab.value ? "opacity-80" : "text-gray-400"}> {tab.count}</span>
            </button>
          ))}
        </div>

        <div className="p-2 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400">
              {search.trim() ? (
                <>
                  Nothing matches <strong>{search.trim()}</strong>.
                </>
              ) : (
                <>
                  No saved {isEmail ? "" : "SMS "}templates yet. Write a message and click{" "}
                  <strong>Save as template</strong>, or add them under Communications &gt; Templates.
                </>
              )}
            </div>
          ) : (
            <>
              <Section title="Collings library" rows={library} />
              <Section title="My templates" rows={mine} />
            </>
          )}
        </div>
      </div>
    </div>
  );

  function Section({ title, rows }) {
    if (!rows.length) return null;
    return (
      <div className="mb-2">
        <p className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">
          {title}
        </p>
        {rows.map((t) => (
          <Row key={t.id} t={t} />
        ))}
      </div>
    );
  }

  function Row({ t }) {
    // Compose-scope tags this template needs that the composer can't fill yet.
    const missing = (t.required_tags || []).filter((name) => !resolvedTags[name]);
    return (
      <button
        type="button"
        onClick={() => onPick(t)}
        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-100"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{t.name}</div>
            <div className="text-xs text-gray-400 line-clamp-2">{body(t) || t.audience_display}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {t.audience_display && (
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-bold text-gray-500">
                {t.audience_display}
              </span>
            )}
            {/* Service vs marketing - Christian: "importantly, have two
                classifications". Marketing is the one that carries the
                unsubscribe obligation. */}
            {t.message_class === "service" && (
              <span
                className="px-1.5 py-0.5 rounded bg-blue-50 text-[10px] font-bold text-blue-600"
                title="Service message - transactional and contact-specific"
              >
                SERVICE
              </span>
            )}
            {t.is_library && (
              <Lock className="w-3 h-3 text-gray-300" title="Shared Collings template" />
            )}
          </div>
        </div>
        {missing.length > 0 && (
          <div className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-600">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Needs {missing.map(labelFor).join(", ")} -{" "}
              {missing.some(isPropertyTag) ? (
                <>
                  <Home className="w-3 h-3 inline -mt-0.5" /> pick a property
                </>
              ) : (
                <>
                  <User className="w-3 h-3 inline -mt-0.5" /> pick the agent to contact
                </>
              )}
            </span>
          </div>
        )}
      </button>
    );
  }
}

// Small local label map for the row hint. Kept minimal on purpose: the
// authoritative labels and reasons come from the merge-tag vocabulary endpoint
// and are shown in full by the composer's pre-send guard.
const TAG_LABELS = {
  PropertyAddress: "property address",
  PropertySuburb: "property suburb",
  Price: "price",
  AuctionDate: "auction date",
  AuctionTime: "auction time",
  OpenTime: "open home time",
  SettlementDate: "settlement date",
  WeeklyRent: "weekly rent",
  PropertyLink: "property link",
  AgentFirstName: "agent name",
  AgentFullName: "agent name",
  AgentMobile: "agent mobile",
  AgentEmail: "agent email",
};

const PROPERTY_TAGS = new Set([
  "PropertyAddress",
  "PropertySuburb",
  "Price",
  "AuctionDate",
  "AuctionTime",
  "OpenTime",
  "SettlementDate",
  "WeeklyRent",
  "PropertyLink",
]);

function labelFor(name) {
  return TAG_LABELS[name] || name;
}

function isPropertyTag(name) {
  return PROPERTY_TAGS.has(name);
}
